/**
 * Account role-management UI.
 *
 * Two exports:
 *
 * - `RoleManagementPanel` — the PRESENTATIONAL roster + promote/demote controls.
 *   Props-driven so the Storybook functional tier can render every roster shape
 *   the backend can return (an `account_owner`, an `account_member`, and a
 *   row-holder with `email: null`) from literal
 *   data — no live daemon, no mock. Each row renders `email ?? user_id` (email is
 *   NULL for a principal with no `users` row) and its `effective_role`;
 *   `account_member` → Promote, `account_owner` → Revoke owner.
 *
 * - `RoleManagementSection` — the fetching container mounted in `SettingsView`.
 *   It resolves the acting account from the JWT claims and populates the panel
 *   from ONE `sdk.accountRoles.list(accountId)` call — the backend reads the
 *   authoritative `account_memberships` population, so
 *   the panel does NO client-side join and never calls the `RequireAdmin`-gated
 *   `sdk.accountManagement.listAccountMembers` (an `account_owner` is not an admin
 *   claim-holder and would get 403). `assign`/`revoke` return the FRESH roster, so
 *   the container writes it straight into the query cache instead of invalidating
 *   and refetching (which would race the async membership-workflow commit across
 *   the pooler — recorded gotcha `reference_byok_setquerydata_race`).
 */

import { useCallback, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAtomValue } from 'jotai';
import { Button } from '@localagent/ui/components/button';
import { Badge } from '@localagent/ui/components/badge';
import {
  Table,
  TableHeader,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
} from '@localagent/ui/components/table';
import { APIError, LastOwnerError } from '@localagent/sdk';
import type { AccountMemberRole } from '@localagent/sdk';
import { getSDK } from '../../services/sdk-instance';
import { claimsAtom, holdsIdentityAtom } from '../../stores/auth/atoms';
import { sdkConfiguredAtom } from '../../stores/sdk/atoms';
import { LoadingState } from '@localagent/ui/components/shogun-spinner';

export interface RoleManagementPanelProps {
  /** The account's full member roster. */
  roster: AccountMemberRole[];
  isLoading?: boolean;
  /** Loud error message for a genuine failure (never the expected non-owner 403). */
  error?: string | null;
  /**
   * True when the caller is authenticated but NOT an account owner (a 403 from
   * the owner-gated list). This is an expected, informational state — not a loud
   * error — so the panel renders a muted note rather than an alarm.
   */
  notOwner?: boolean;
  /** `user_id` of the row currently mutating (its buttons disable). */
  busyUserId?: string | null;
  /** Promote a principal to `account_owner`. */
  onPromote: (userId: string) => void;
  /** Revoke owner standing; the principal stays an `account_member`. */
  onRevoke: (userId: string) => void;
}

export function RoleManagementPanel({
  roster,
  isLoading = false,
  error = null,
  notOwner = false,
  busyUserId = null,
  onPromote,
  onRevoke,
}: RoleManagementPanelProps) {
  if (notOwner) {
    return (
      <p
        className="text-body text-muted-foreground"
        data-testid="role-management-not-owner"
      >
        Only account owners can view and manage account roles.
      </p>
    );
  }

  if (error) {
    return (
      <p className="text-body text-destructive" data-testid="role-management-error">
        {error}
      </p>
    );
  }

  if (isLoading) {
    return (
      <LoadingState data-testid="role-management-loading" label="Loading account roster…" />
    );
  }

  return (
    <div className="rounded-lg" data-testid="role-management-panel">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Member</TableHead>
            <TableHead>Role</TableHead>
            <TableHead className="text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {roster.length === 0 ? (
            <TableRow>
              <TableCell colSpan={3} className="text-center text-body text-muted-foreground">
                <span data-testid="role-management-empty">No members in this account.</span>
              </TableCell>
            </TableRow>
          ) : (
            roster.map((member) => {
              const busy = busyUserId === member.user_id;
              const isOwner = member.effective_role === 'account_owner';
              return (
                <TableRow key={member.user_id} data-testid={`role-row-${member.user_id}`}>
                  <TableCell className="font-mono text-caption">
                    {member.email ?? member.user_id}
                  </TableCell>
                  <TableCell>
                    <Badge
                      variant={isOwner ? 'default' : 'secondary'}
                      data-testid={`role-badge-${member.user_id}`}
                    >
                      {member.effective_role}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    {isOwner ? (
                      <Button
                        variant="ghost"
                        size="sm"
                        disabled={busy}
                        data-testid={`role-revoke-${member.user_id}`}
                        onClick={() => onRevoke(member.user_id)}
                      >
                        {busy ? 'Revoking…' : 'Revoke owner'}
                      </Button>
                    ) : (
                      <Button
                        variant="ghost"
                        size="sm"
                        disabled={busy}
                        data-testid={`role-promote-${member.user_id}`}
                        onClick={() => onPromote(member.user_id)}
                      >
                        {busy ? 'Promoting…' : 'Promote to owner'}
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              );
            })
          )}
        </TableBody>
      </Table>
    </div>
  );
}

/**
 * Fetching container mounted in `SettingsView`. Resolves the acting account from
 * the JWT claims and drives {@link RoleManagementPanel} off a single
 * `sdk.accountRoles.list` call.
 */
export function RoleManagementSection() {
  const isAuthenticated = useAtomValue(holdsIdentityAtom);
  const sdkConfigured = useAtomValue(sdkConfiguredAtom);
  const claims = useAtomValue(claimsAtom);
  const accountId = claims?.accountId ?? null;
  const queryClient = useQueryClient();

  const [busyUserId, setBusyUserId] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const queryKey = ['account-roles', accountId] as const;

  const rosterQuery = useQuery<AccountMemberRole[], Error>({
    queryKey,
    queryFn: () => getSDK().accountRoles.list(accountId!),
    enabled: Boolean(isAuthenticated && sdkConfigured && accountId),
    // A non-owner's 403 is an expected terminal answer, not a transient failure —
    // do not retry it.
    retry: (failureCount, err) =>
      !(err instanceof APIError && err.status === 403) && failureCount < 2,
  });

  // A 403 from the owner-gated list means "you are not an account owner" — an
  // expected informational state, distinct from a genuine failure.
  const notOwner =
    rosterQuery.error instanceof APIError && rosterQuery.error.status === 403;
  const loadError = notOwner ? null : rosterQuery.error?.message ?? null;

  const mutate = useCallback(
    async (userId: string, op: Promise<AccountMemberRole[]>) => {
      if (!accountId) return;
      setBusyUserId(userId);
      setActionError(null);
      try {
        const fresh = await op;
        // assign/revoke return the FRESH roster — write it straight into the
        // cache; do NOT invalidate+refetch (races the async workflow commit).
        queryClient.setQueryData(queryKey, fresh);
      } catch (err) {
        if (err instanceof LastOwnerError) {
          setActionError(
            'This is the last account owner. Promote another member to owner first, then revoke.',
          );
        } else {
          setActionError(err instanceof Error ? err.message : String(err));
        }
      } finally {
        setBusyUserId(null);
      }
    },
    [accountId, queryClient, queryKey],
  );

  const onPromote = useCallback(
    (userId: string) => {
      if (!accountId) return;
      void mutate(userId, getSDK().accountRoles.assign(accountId, userId, 'account_owner'));
    },
    [accountId, mutate],
  );

  const onRevoke = useCallback(
    (userId: string) => {
      if (!accountId) return;
      void mutate(userId, getSDK().accountRoles.revoke(accountId, userId));
    },
    [accountId, mutate],
  );

  if (!isAuthenticated || !accountId) {
    return (
      <section className="space-y-4">
        <div>
          <h2 className="text-title-2 font-semibold">Account Roles</h2>
          <p className="text-body text-muted-foreground">
            Sign in to manage account roles.
          </p>
        </div>
      </section>
    );
  }

  return (
    <section className="space-y-4" data-testid="role-management-section">
      <div>
        <h2 className="text-title-2 font-semibold">Account Roles</h2>
        <p className="text-body text-muted-foreground">
          Account owners can promote members to owner or revoke a member&apos;s
          role. Owners can archive workspaces; the last owner cannot be removed.
        </p>
      </div>

      {actionError && (
        <p className="text-body text-destructive" data-testid="role-management-action-error">
          {actionError}
        </p>
      )}

      <RoleManagementPanel
        roster={rosterQuery.data ?? []}
        isLoading={rosterQuery.isLoading}
        error={loadError}
        notOwner={notOwner}
        busyUserId={busyUserId}
        onPromote={onPromote}
        onRevoke={onRevoke}
      />
    </section>
  );
}
